import type { ToastOptions } from "@/components/ui/toast"

/**
 * EVERY message the app can put in front of a reader, in one list.
 *
 * Before this, each message was defined wherever it happened to be raised, which meant nobody
 * could see the set. Listing them showed what that costs: of 23 distinct strings, 5 used a
 * contraction ("Couldn't…") and 2 spelled it out ("Could not…"); 12 ended in a full stop, 10 in
 * nothing, and 1 in an exclamation mark. `research.threadLoadFailed` and
 * `research.conversationLoadFailed` below are the same event worded two different ways in two
 * files — a duplicate that was invisible until the two sat on the same screen.
 *
 * THE WORDING HERE IS UNCHANGED from what each call site said. Normalising it is a copy pass,
 * and a copy pass wants one person reading the whole list and deciding the house style — not me
 * quietly rewriting 23 strings while moving them. The list is the deliverable; the edit is next.
 *
 * ── Static entries and factories ──────────────────────────────────────────────────────────
 *
 * Some messages are fixed. Others cannot be: the server supplies the whole string
 * (`projects.failed`), or the subject varies (`preferences.updateFailed`). Those are functions
 * rather than being left at the call site, so the catalogue still owns their SHAPE and their
 * fallback wording, and this file remains a complete account of what the app can say. A partial
 * list would be worse than none, because it would look complete.
 *
 * ── Tone ──────────────────────────────────────────────────────────────────────────────────
 *
 * Preserved exactly as each call site had it. Worth noting for the copy pass: the validation
 * messages ("Please enter a research question.") are `error`, because they were raised through
 * sonner's `toast.error`. They are arguably `warning` — nothing failed, the form is just
 * incomplete — but that is a behaviour change, so it is yours to make rather than mine.
 */

/** Identity. Exists only so each entry is checked against ToastOptions where it is written. */
const msg = (options: ToastOptions): ToastOptions => options

export const MESSAGES = {
  /* ── signing in and out ───────────────────────────────────────────────────────────────── */
  auth: {
    signedOut: msg({
      textKey: "auth.signedOut.text",
      descriptionKey: "auth.signedOut.description",
      tone: "info",
      dismiss: true,
    }),
    accountDeleted: msg({
      textKey: "auth.accountDeleted.text",
      descriptionKey: "auth.accountDeleted.description",
      tone: "info",
      dismiss: true,
    }),
    accountActivated: msg({
      textKey: "auth.accountActivated.text",
      descriptionKey: "auth.accountActivated.description",
      tone: "success",
      dismiss: true,
    }),
    /** Every refusal on the two auth forms, from a missing field to a server error. */
    problem: (description: string) =>
      msg({ textKey: "auth.problem.text", description, tone: "error", dismiss: true }),
    missingEmail: "Please enter your email.",
    missingPassword: "Please enter your password.",
    termsNotAccepted: "You must accept the terms and conditions to continue.",
    unexpected: "An unexpected error occurred",
  },

  /* ── bookmarks ────────────────────────────────────────────────────────────────────────── */
  bookmark: {
    saved: msg({ textKey: "bookmark.saved.text", tone: "success" }),
    removed: msg({ textKey: "bookmark.removed.text", tone: "success" }),
    signInRequired: msg({ textKey: "bookmark.signInRequired.text", tone: "warning" }),
    loadFailed: msg({ textKey: "bookmark.loadFailed.text", tone: "error" }),
    // The server's own reason wins where there is one: it says which constraint or column was
    // the problem, and the generic line says nothing the reader did not already infer.
    saveFailed: (reason?: string) =>
      msg({ text: reason, textKey: "bookmark.saveFailed.text", tone: "error" }),
    removeFailed: (reason?: string) =>
      msg({ text: reason, textKey: "bookmark.removeFailed.text", tone: "error" }),
  },

  /* ── feedback ─────────────────────────────────────────────────────────────────────────── */
  feedback: {
    sent: msg({ textKey: "feedback.sent.text", tone: "success" }),
    empty: msg({ textKey: "feedback.empty.text", tone: "warning" }),
    signInRequired: msg({ textKey: "feedback.signInRequired.text", tone: "warning" }),
    failed: msg({ textKey: "feedback.failed.text", tone: "error" }),
  },

  /* ── copying ──────────────────────────────────────────────────────────────────────────── */
  clipboard: {
    copied: msg({ textKey: "clipboard.copied.text", tone: "success" }),
    failed: msg({ textKey: "clipboard.failed.text", tone: "error" }),
  },

  /* ── research ─────────────────────────────────────────────────────────────────────────── */
  research: {
    rateLimited: msg({
      textKey: "research.rateLimited.text",
      descriptionKey: "research.rateLimited.description",
      tone: "error",
    }),
    tooManyInProgress: (reason?: string) =>
      msg({ textKey: "research.tooManyInProgress.text", description: reason, tone: "error" }),
    jobFailed: (reason?: string) =>
      msg({ textKey: "research.jobFailed.text", description: reason, tone: "error" }),
    progressFailed: (reason?: string) =>
      msg({ textKey: "research.progressFailed.text", description: reason, tone: "error" }),
    submitFailed: (reason?: string) =>
      msg({ textKey: "research.submitFailed.text", description: reason, tone: "error" }),
    searchFailed: (reason?: string) =>
      msg({ textKey: "research.searchFailed.text", description: reason, tone: "error" }),
    revisionSubmitted: msg({ textKey: "research.revisionSubmitted.text", tone: "success" }),

    // These two are the SAME EVENT, worded differently in two files — ThreadSheet said one and
    // useResearchJob the other. Left as they were for the copy pass to collapse; they are
    // adjacent here so the choice is visible.
    threadLoadFailed: (reason?: string) =>
      msg({ textKey: "research.threadLoadFailed.text", description: reason, tone: "error" }),
    conversationLoadFailed: (reason?: string) =>
      msg({ textKey: "research.conversationLoadFailed.text", description: reason, tone: "error" }),

    missingQuestion: msg({ textKey: "research.missingQuestion.text", tone: "error" }),
    missingClarification: msg({ textKey: "research.missingClarification.text", tone: "error" }),
    missingFollowUp: msg({ textKey: "research.missingFollowUp.text", tone: "error" }),
    missingRevision: msg({ textKey: "research.missingRevision.text", tone: "error" }),
  },

  /* ── reading ──────────────────────────────────────────────────────────────────────────── */
  read: {
    noPropositionId: msg({
      textKey: "read.noPropositionId.text",
      descriptionKey: "read.noPropositionIdDescription",
      tone: "info",
      dismiss: true,
    }),

    /* ── generating a document's AI analyses (EnrichButton) ──────────────────────────────── */

    // A factory: the service's own reason is worth more than a generic failure. A timeout and
    // a missing document are different things to be told, and only the server knows which.
    enrichFailed: (reason?: string) =>
      msg({
        textKey: "read.noPropositionId.text",
        description: reason ?? "Please try again.",
        tone: "error",
        dismiss: true,
      }),
    enrichEmpty: msg({
      textKey: "read.enrichEmpty.text",
      descriptionKey: "read.enrichEmpty.description",
      tone: "error",
      dismiss: true,
    }),
    enrichNoDocumentId: msg({
      textKey: "read.enrichNoDocumentId.text",
      descriptionKey: "read.enrichNoDocumentId.description",
      tone: "error",
      dismiss: true,
    }),
  },

  /* ── search ───────────────────────────────────────────────────────────────────────────── */
  search: {
    missingQuery: msg({ textKey: "search.missingQuery.text", tone: "error" }),
    // `execute_search` raised this same event as "Search Failed" — different capitalisation,
    // different punctuation, same failure. Collapsed onto the live wording rather than
    // catalogued twice; that one is a duplicate, not a choice.
    failed: (reason?: string) => msg({ textKey: "search.failed.text", description: reason, tone: "error" }),
  },

  /* ── projects ─────────────────────────────────────────────────────────────────────────── */
  projects: {
    // The server writes these outright; the fallbacks are what shows when it does not.
    created: (message?: string) => msg({ text: message, textKey: "projects.created.text", tone: "success" }),
    updated: (message?: string) => msg({ text: message, textKey: "projects.updated.text", tone: "success" }),
    deleted: (message?: string) => msg({ text: message, textKey: "projects.deleted.text", tone: "success" }),
    failed: (message: string) => msg({ text: message, tone: "error" }),
  },

  /* ── the account page ─────────────────────────────────────────────────────────────────── */
  account: {
    emailChangeSent: (message?: string) =>
      msg({
        text: message, textKey: "account.text.text",
        descriptionKey: "account.description",
        tone: "success",
      }),
    passwordUpdated: (message?: string) =>
      msg({ text: message, textKey: "account.passwordUpdated.text", tone: "success" }),
    nameUpdated: msg({ textKey: "account.nameUpdated.text", tone: "success" }),
    deleted: msg({ textKey: "account.deleted.text", tone: "success" }),
    historyCleared: msg({ textKey: "account.historyCleared.text", tone: "success" }),
    /** Every account action reports its failure with the server's own reason. */
    failed: (message: string) => msg({ text: message, tone: "error" }),
  },
} as const
